import { useEffect, useState } from "react";

interface UseCompletionLoadingStateOptions {
    isLoading?: boolean;
    minLoadingTime?: number;
    loadingSteps?: readonly string[];
    stepIntervalMs?: number;
    loadingText?: string;
    fallbackHeadline?: string;
}

interface CompletionLoadingState {
    showLoading: boolean;
    loadingHeadline: string;
}

/**
 * Shared loading timer used by completion screens so refetch and decorative
 * step sequences resolve to one consistent ready state.
 */
export function useCompletionLoadingState({
    isLoading = false,
    minLoadingTime = 0,
    loadingSteps,
    stepIntervalMs = 1000,
    loadingText,
    fallbackHeadline = "",
}: UseCompletionLoadingStateOptions): CompletionLoadingState {
    const stepCount = loadingSteps?.length ?? 0;
    const [isMinTimeElapsed, setIsMinTimeElapsed] = useState(minLoadingTime === 0 && stepCount === 0);
    const [stepIndex, setStepIndex] = useState(0);

    useEffect(() => {
        if (minLoadingTime === 0 && stepCount === 0) {
            setIsMinTimeElapsed(true);
            return;
        }

        setIsMinTimeElapsed(false);
        setStepIndex(0);

        if (stepCount === 0) {
            const timer = setTimeout(() => setIsMinTimeElapsed(true), minLoadingTime);
            return () => clearTimeout(timer);
        }

        const timers: ReturnType<typeof setTimeout>[] = [];
        for (let i = 1; i < stepCount; i++) {
            timers.push(setTimeout(() => setStepIndex(i), i * stepIntervalMs));
        }
        timers.push(setTimeout(() => setIsMinTimeElapsed(true), stepCount * stepIntervalMs));
        return () => timers.forEach(clearTimeout);
    }, [stepCount, stepIntervalMs, minLoadingTime]);

    const showLoading =
        stepCount > 0 ? !isMinTimeElapsed || isLoading : isLoading || (minLoadingTime > 0 && !isMinTimeElapsed);

    const loadingHeadline =
        stepCount > 0 && loadingSteps
            ? (loadingSteps[Math.min(stepIndex, stepCount - 1)] ?? loadingText ?? fallbackHeadline)
            : (loadingText ?? fallbackHeadline);

    return { showLoading, loadingHeadline };
}
